import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, KeyRound, CheckCircle2 } from "lucide-react";
import client, { errText } from "../lib/api";

/**
 * Reset password. Reads the single-use token from the link, takes a
 * new password (12+ chars, confirmed), and sends both to the backend.
 * On success it points the person back to sign in with the new
 * password. Every live session was already revoked server side.
 */
export default function ResetPasswordScreen() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const tooShort = pw.length > 0 && pw.length < 12;
  const mismatch = confirm.length > 0 && confirm !== pw;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 12) { setErr("Use at least twelve characters."); return; }
    if (pw !== confirm) { setErr("Those two passwords do not match."); return; }
    setBusy(true);
    try {
      await client.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
    } catch (e2) {
      setErr(errText(e2, "That reset link is invalid or has expired. Request a new one."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="k-auth-shell" data-testid="reset-screen">
      <div className="k-auth-grid" aria-hidden="true" />
      <div className="k-auth-grain" aria-hidden="true" />
      <div className="relative max-w-md mx-auto px-5 py-16 z-10">
        <div className="k-auth-card">
          <div className="p-6 sm:p-7">
            {done ? (
              <div className="text-center" data-testid="reset-done">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: "hsl(var(--klareet-teal) / 0.14)", color: "hsl(var(--klareet-teal))" }}>
                  <CheckCircle2 size={24} />
                </div>
                <h1 className="klareet-serif mb-1" style={{ fontSize: "1.5rem", color: "hsl(var(--klareet-ink))" }}>You are all set.</h1>
                <p className="k-quiet mb-5">Your password is updated. Sign in with the new one.</p>
                <button onClick={() => nav("/")} className="k-btn k-btn-accent w-full" data-testid="reset-signin">
                  Go to sign in
                </button>
              </div>
            ) : !token ? (
              <div data-testid="reset-no-token">
                <h1 className="klareet-serif mb-1" style={{ fontSize: "1.4rem", color: "hsl(var(--klareet-ink))" }}>This link looks off.</h1>
                <p className="k-quiet mb-5">The reset link is missing its token. Request a fresh one.</p>
                <button onClick={() => nav("/forgot-password")} className="k-btn k-btn-outline w-full" data-testid="reset-request-new">
                  Request a new link
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-1" style={{ background: "hsl(var(--klareet-teal) / 0.12)", color: "hsl(var(--klareet-teal))" }}>
                  <KeyRound size={20} />
                </div>
                <h1 className="klareet-serif" style={{ fontSize: "1.5rem", color: "hsl(var(--klareet-ink))" }} data-testid="reset-title">Set a new password</h1>
                <div>
                  <label className="k-label" htmlFor="reset-pw">New password</label>
                  <div className="k-input-wrap mt-1">
                    <input
                      id="reset-pw"
                      type={show ? "text" : "password"}
                      className="k-input k-input-pw"
                      value={pw}
                      onChange={(e) => setPw(e.target.value)}
                      placeholder="at least twelve characters"
                      autoComplete="new-password"
                      required
                      data-testid="reset-password"
                    />
                    <button type="button" className="k-input-eye" onClick={() => setShow((v) => !v)} aria-label={show ? "Hide" : "Show"} tabIndex={-1} data-testid="reset-password-toggle">
                      {show ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {tooShort && <div className="k-quiet !text-xs mt-1" style={{ color: "hsl(var(--klareet-amber))" }}>A little longer, twelve characters or more.</div>}
                </div>
                <div>
                  <label className="k-label" htmlFor="reset-confirm">Confirm password</label>
                  <input
                    id="reset-confirm"
                    type={show ? "text" : "password"}
                    className="k-input mt-1"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="type it again"
                    autoComplete="new-password"
                    required
                    data-testid="reset-confirm"
                  />
                  {mismatch && <div className="k-quiet !text-xs mt-1" style={{ color: "hsl(var(--klareet-amber))" }}>Those do not match yet.</div>}
                </div>
                {err && (
                  <div className="text-sm px-3 py-2 rounded-lg" style={{ color: "hsl(0 60% 40%)", background: "hsl(0 60% 96%)", border: "1px solid hsl(0 60% 88%)" }} data-testid="reset-error">
                    {err}
                  </div>
                )}
                <button type="submit" disabled={busy} className="k-btn k-btn-accent w-full" data-testid="reset-submit">
                  {busy ? "Saving." : "Set new password"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
